import { useState, useCallback } from 'react';
import { createMessage } from '../../utils/messageUtils';

export const useChatState = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showFormPreview, setShowFormPreview] = useState(false);

  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const addUserMessage = useCallback((content) => {
    const message = createMessage('user', content);
    addMessage(message);
    return message;
  }, [addMessage]);

  const addAIMessage = useCallback((content, extractedData = {}, additionalProps = {}) => {
    const message = createMessage('ai', content, extractedData, additionalProps);
    addMessage(message);
    return message;
  }, [addMessage]);

  const updateExtractedData = useCallback((newData) => {
    setExtractedData(prev => ({ ...prev, ...newData }));
  }, []);

  const updateConversationHistory = useCallback((userContent, assistantContent) => {
    setConversationHistory(prev => [...prev, 
      { role: 'user', content: userContent },
      { role: 'assistant', content: assistantContent }
    ]);
  }, []);

  const initializeChat = useCallback((locations = []) => {
    // Build locations section
    let locationsSection = '';
    if (locations && locations.length > 0) {
      locationsSection = `\n\n📍 **UBICACIONES DISPONIBLES:**\n${locations.map((loc, index) => `• Ubicación ${index + 1}: ${loc.address}`).join('\n')}\n`;
    } else {
      locationsSection = '\n\n📍 **UBICACIÓN:** Se seleccionará al final del proceso\n';
    }

    const welcomeMessage = createMessage('ai', 
      `¡Hola! Soy tu asistente de IA para crear ofertas de trabajo. Te ayudo a crear la oferta completa con toda la información necesaria.

        📋 **INFORMACIÓN QUE NECESITO:**

        👨‍💼 **POSICIÓN** - Elige una opción:
        • Chef Ejecutivo • Sous Chef • Jefe de Cocina • Maestro de Cocina • Maestro Pastelero • Pastelero • Panadero • Repostero • Charcutero • Pizzero • Itamae • Sushiman • Ayudante de Sushi • Parrillero • Cocinero Frío • Cocinero Caliente • Manipulador de Alimentos • Encargado de Producción • Operador de Cocina • Operador de Planta • Operador Multifuncional • Encargado de Reservas • Recepcionista de Restaurante • Supervisor de Salón • Personal de Banquetería • Encargado de Bodega • Repositor • Personal de Mantenimiento • Jefe de Local • Administrador de Local • Jefe de Sucursales • Administrador de Restaurante • Encargado de Compras • Control de Calidad • Catador de Vinos • Coordinador de Banquetes • Montajista • Mixólogo

        📅 **HORARIO** - Elige una opción:
        • Full-time (tiempo completo) • Part-time (medio tiempo) • Otro

        💰 **SALARIO** - Solo el número (ej: 1200000)

        📋 **CONTRATO** - Elige una opción:
        • A Plazo • Indefinido • Honorarios • Práctica • Otros

        👥 **VACANTES** - Cuántos puestos necesitas (ej: 2)

        🎯 **EXPERIENCIA** - Elige una opción:
        • Sin experiencia (0) • 1 año • 2 años • 3 años • 4 años • +5 años

        ⏰ **PERÍODO** - Elige una opción:
        • Permanente • Reemplazo Temporal • Reemplazo Urgente • Sin información

        📝 **DESCRIPCIÓN** - Describe el trabajo y ambiente laboral

        ⚙️ **FUNCIONES** - Lista las tareas principales

        ✅ **REQUISITOS** - Qué necesita el candidato

        ❓ **PREGUNTAS** - Al menos 1 pregunta para candidatos${locationsSection}
        💡 **TIP:** Puedes proporcionar toda la información de una vez o ir completando campo por campo.

        ¿Qué tipo de trabajo necesitas publicar?`
    );
    setMessages([welcomeMessage]);
  }, []);

  const resetChat = useCallback(() => {
    setMessages([]);
    setInputMessage('');
    setExtractedData({});
    setConversationHistory([]);
    setShowFormPreview(false);
    setIsLoading(false);
  }, []);

  return {
    // State
    messages,
    inputMessage,
    isLoading,
    extractedData,
    conversationHistory,
    showFormPreview,
    
    // Setters
    setInputMessage,
    setIsLoading,
    setShowFormPreview,
    
    // Actions
    addMessage,
    addUserMessage,
    addAIMessage,
    updateExtractedData,
    updateConversationHistory,
    initializeChat,
    resetChat
  };
};
